import React, { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, TextField, Button, Divider, Typography, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AddCircle, ChevronLeft, ChevronRight, Delete } from "@mui/icons-material";
import Datagrid from "../Datagrid/Datagrid";
import { LABELS, TABLE_HEADERS, TITLES } from "../../constants";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import {
    getCustomersCusSites,
    updateCustomer,
    createCustomer,
    deleteCustomers,
    downloadCustomerTemplate,
    bulkUploadCustomers
} from "../../services/manageCustomerMasterService";
const ManageCustomersMaster = () => {
    const [allCustomers, setAllCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selection, setSelection] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formAlert, setFormAlert] = useState(null);
    const [form, setForm] = useState({
        customer_number: "",
        customer_name: "",
        address_name_cus: "",
        address_full_cus: "",
        street1_cus: "",
        street2_cus: "",
        street3_cus: "",
        county_cus: "",
        city_cus: "",
        zip_cus: "",
        state_cus: "",
        country_cus: "",
        po_box_cus: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const { t } = useTranslation();
    const { fetchSites } = useSite();

    useEffect(() => {
        const fetchAllCustomers = async () => {
            try {
                const response = await getCustomersCusSites();
                setAllCustomers(response.data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAllCustomers();
    }, []);

    const handleEdit = (row) => {
        setEditRow(row);
        setForm({
            customer_number: row.customer_number || "",
            customer_name: row.customer_name || "",
            address_name_cus: row.address_name_cus || "",
            address_full_cus: row.address_full_cus || "",
            street1_cus: row.street1_cus || "",
            street2_cus: row.street2_cus || "",
            street3_cus: row.street3_cus || "",
            county_cus: row.county_cus || "",
            city_cus: row.city_cus || "",
            zip_cus: row.zip_cus || "",
            state_cus: row.state_cus || "",
            country_cus: row.country_cus || "",
            po_box_cus: row.po_box_cus || "",
        });
        setFormAlert(null);
        setDialogOpen(true);
    };

    const handleAdd = () => {
        setEditRow(null);
        setForm({
            customer_number: "",
            customer_name: "",
            address_name_cus: "",
            address_full_cus: "",
            street1_cus: "",
            street2_cus: "",
            street3_cus: "",
            county_cus: "",
            city_cus: "",
            zip_cus: "",
            state_cus: "",
            country_cus: "",
            po_box_cus: "",
        });
        setFormErrors({});
        setFormAlert(null);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            const nextErrors = {};
            if (!form.customer_number) nextErrors.customer_number = t('GENERAL.CUSTOMER_NUMBER_IS_REQUIRED');
            if (!form.customer_name) nextErrors.customer_name = t('GENERAL.CUSTOMER_NAME_IS_REQUIRED');
            if (Object.keys(nextErrors).length > 0) {
                setFormErrors(nextErrors);
                setFormAlert({ severity: "warning", message: t('GENERAL.PLEASE_FILL_THE_REQUIRED_FIELDS') });
                Swal.fire({
                    icon: "warning",
                    title: t('ALERT_MESSAGES.WARNING'),
                    text: t('GENERAL.PLEASE_FILL_THE_REQUIRED_FIELDS'),
                });
                return;
            }
            const nextNumber = `${form.customer_number || ""}`.trim().toLowerCase();
            const hasDuplicate = allCustomers.some((customer) => {
                if (!customer?.customer_number) return false;
                if (editRow && customer.cus_id === editRow.cus_id) return false;
                return String(customer.customer_number).trim().toLowerCase() === nextNumber;
            });
            if (hasDuplicate) {
                setFormAlert({ severity: "error", message: t('GENERAL.CUSTOMER_NUMBER_ALREADY_EXISTS') });
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('GENERAL.CUSTOMER_NUMBER_ALREADY_EXISTS'),
                });
                return;
            }
            setIsSubmitting(true);
            if (editRow) {
                await updateCustomer(editRow.cus_id, form);
            } else {
                await createCustomer(form);
            }
            const resp = await getCustomersCusSites();
            setAllCustomers(resp.data);
            await fetchSites();
            setDialogOpen(false);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: editRow ? t('GENERAL.CUSTOMER_UPDATED_SUCCESSFULLY') : t('GENERAL.CUSTOMER_CREATED_SUCCESSFULLY'),
            });
        } catch (e) {
            console.error(e);
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('GENERAL.FAILED_TO_SAVE_CUSTOMER'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (selection.length === 0) return;
        try {
            const confirmation = await Swal.fire({
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                text: t('GENERAL.THIS_WILL_DELETE_THE_CUSTOMERS_PERMANENTLY'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: t('GENERAL.YES_DELETE_IT'),
            });
            if (!confirmation.isConfirmed) return;
            await deleteCustomers(selection);
            const resp = await getCustomersCusSites();
            setAllCustomers(resp.data);
            await fetchSites();
            setSelection([]);
            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('GENERAL.CUSTOMERS_DELETED_SUCCESSFULLY'),
            });
        } catch (e) { console.error(e); }
    };

    const handleDownloadTemplate = async () => {
        try {
            const resp = await downloadCustomerTemplate();
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', 'customers_template.csv');
            document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) { console.error(e); }
    };

    const handleUploadCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('file', file);
        try {
            await bulkUploadCustomers(fd);
            const resp = await getCustomersCusSites();
            setAllCustomers(resp.data);
        } catch (err) { console.error(err); }
    };

    const columns = [
        { field: "customer_number", headerName: t('TABLE_HEADERS.CUSTOMER_NUMBER'), flex: 1 },
        { field: "customer_name", headerName: t('TABLE_HEADERS.CUSTOMER_NAME'), flex: 1 },
        // { field: "invoice_account", headerName: "Invoice Account", flex: 1 },
        // { field: "parent_customer", headerName: "Parent Customer", flex: 1 },
        { field: "address_full_cus", headerName: t('TABLE_HEADERS.CUSTOMER_ADDRESS'), flex: 1 },
        {
            headerName: t('TABLE_HEADERS.ACTIONS'),
            sortable: false,
            width: 100, // Ensure there's enough space for icons
            headerAlign: "center",
            align: "left",
            renderCell: (params) => (
                <div>
                    <Tooltip title={t('TITLES.EDIT_CUSTOMER')}>
                        <IconButton
                            color="primary"
                            aria-label="Edit Customer"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleEdit(params.row);
                            }}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const customButtons = [
        { label: 'Add', icon: <AddCircle />, onClick: handleAdd },
        // { label: 'Download Template', icon: <ChevronRight />, onClick: handleDownloadTemplate },
        // { label: 'Upload CSV', icon: <ChevronLeft />, onClick: () => { }, upload: true, acceptedFile: '.csv', handleFileChange: handleUploadCSV },
        { label: t('LABELS.DELETE_SELECTED'), icon: <Delete />, onClick: handleDelete },
    ];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
                <CircularProgress />
            </Box>
        );
    }
    return (
        <div>
            <Datagrid
                loading={loading}
                data={allCustomers}
                columns={columns}
                customButtons={customButtons}
                checkboxSelection={true}
                rowIdField="cus_id"
                onSelect={setSelection}
                sortBy={[{ field: "cus_id", sort: "asc" }]}
                hideFilterFromColumnMenu
                lockActiveColumnInManageColumns
                preventHidingLastColumn
            />
            <ModalWrapper
                open={dialogOpen}
                setOpen={setDialogOpen}
                title={editRow ? t('TITLES.EDIT_CUSTOMER') : t('TITLES.ADD_CUSTOMER')}
                content={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {formAlert && (
                            <Alert severity={formAlert.severity || "error"}>{formAlert.message}</Alert>
                        )}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{t('UI_TEXT.CUSTOMER_DETAILS')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('GENERAL.BASIC_DEATILS_FOR_THE_CUSTOMER_TO_BE_ADDED')}
                            </Typography>
                        </Box>
                        <TextField
                            label={t('TABLE_HEADERS.CUSTOMER_NUMBER')}
                            value={form.customer_number}
                            onChange={e => setForm({ ...form, customer_number: e.target.value })}
                            error={Boolean(formErrors.customer_number)}
                            helperText={formErrors.customer_number || ""}
                            size="small"
                        />
                        <TextField
                            label={t('TABLE_HEADERS.CUSTOMER_NAME')}
                            value={form.customer_name}
                            onChange={e => setForm({ ...form, customer_name: e.target.value })}
                            error={Boolean(formErrors.customer_name)}
                            helperText={formErrors.customer_name || ""}
                            size="small"
                        />
                        <Divider />
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>{t('LABELS.ADDRESS')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('GENERAL.THE_DEFAULT_ADDRESS_FOR_THE_CUSTOMER')}
                            </Typography>
                        </Box>
                        <TextField label={t('UI_TEXT.ADDRESS_NAME')} value={form.address_name_cus} onChange={e => setForm({ ...form, address_name_cus: e.target.value })} size="small" />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField label={t('LABELS.STREET')} value={form.street1_cus} onChange={e => setForm({ ...form, street1_cus: e.target.value })} size="small" fullWidth />
                            <TextField label={t('GENERAL.STREET_2')} value={form.street2_cus} onChange={e => setForm({ ...form, street2_cus: e.target.value })} size="small" fullWidth />
                            <TextField label={t('GENERAL.STREET_3')} value={form.street3_cus} onChange={e => setForm({ ...form, street3_cus: e.target.value })} size="small" fullWidth />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField label={t('GENERAL.CITY')} value={form.county_cus} onChange={e => setForm({ ...form, county_cus: e.target.value })} size="small" fullWidth />
                            <TextField label={t('LABELS.CITY')} value={form.city_cus} onChange={e => setForm({ ...form, city_cus: e.target.value })} size="small" fullWidth />
                            <TextField label={t('LABELS.STATE')} value={form.state_cus} onChange={e => setForm({ ...form, state_cus: e.target.value })} size="small" fullWidth />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField label={t('LABELS.ZIP')} value={form.zip_cus} onChange={e => setForm({ ...form, zip_cus: e.target.value })} size="small" fullWidth />
                            <TextField label={t('LABELS.COUNTRY')} value={form.country_cus} onChange={e => setForm({ ...form, country_cus: e.target.value })} size="small" fullWidth />
                            <TextField label="PO Box" value={form.po_box_cus} onChange={e => setForm({ ...form, po_box_cus: e.target.value })} size="small" fullWidth />
                        </Box>
                    </Box>
                }
                actions={
                    <>
                        <Button onClick={() => setDialogOpen(false)} disabled={isSubmitting}>{t('BUTTON.CANCEL')}</Button>
                        <Button variant="contained" onClick={handleSave} disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                                    {t('BUTTON.SUBMITTING')}
                                </>
                            ) : (
                                t('BUTTON.SAVE')
                            )}
                        </Button>
                    </>
                }
            />
        </div>
    );
};

export default ManageCustomersMaster;